import { KeyValuePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Output, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { getSelectedExhibitionId, getSelectedStep, getStepsValidity, selectCurrencySettings } from '@app/app.reducer';
import { LsButtonComponent } from '@axess/ng-limestone';
import { BaseRoute, StepRoute } from '@core/data-access/models/routes.model';
import { Store, select } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ProductType } from '@products/models/products.model';
import { FormatPricePipe } from '@shared/pipes/format-price/format-price.pipe';
import { HideTranslationPipe } from '@shared/pipes/hide-translation/hide-translation.pipe';
import { getAllBookedPackagePricesGroupedByPackageIndex } from '@store/products/booking/product/package/package-booking.selectors';
import {
  getAllBookedProductsTotalPrice,
  getSortedBasketProducts
} from '@store/products/booking/product/product-booking.selectors';
import { selectIsPromoCodeDiscountApplied } from '@store/products/booking/smart-promotion/booking-smart-promotion.selectors';
import { StepsFormsService } from '@store/step-forms/steps-forms.service';
import { isEmpty } from 'lodash';
import { combineLatest, debounceTime, filter, first, map } from 'rxjs';

@Component({
  selector: 'app-basket',
  templateUrl: './basket.component.html',
  styleUrls: ['./basket.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    NgTemplateOutlet,
    TranslateModule,
    HideTranslationPipe,
    FormatPricePipe,
    KeyValuePipe,
    LsButtonComponent
  ]
})
export class BasketComponent {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly stepFormsService = inject(StepsFormsService);

  @Output() isBasketOpenEvent = new EventEmitter<boolean>();

  ProductType = ProductType;

  selectedStepName = this.store.selectSignal(getSelectedStep);
  sortedBookedProducts = this.store.selectSignal(getSortedBasketProducts);
  allBookedPackagePricesGroupedByPackageIndex = this.store.selectSignal(getAllBookedPackagePricesGroupedByPackageIndex);
  isNextButtonDisabled = this.setNextButtonDisabledSignal();
  currencySettingsSignal = this.setCurrencySettingsSignal();
  bookedProductsTotalPriceState = this.setBookedProductsTotalPriceStateSignal();

  toTicketSelection() {
    this.isBasketOpenEvent.emit(false);

    this.store
      .select(getSelectedExhibitionId)
      .pipe(first())
      .subscribe((selectedExhibitionId) =>
        this.router.navigate([`/${BaseRoute.WebShop}/${selectedExhibitionId}/${StepRoute.Tickets}`])
      );
  }

  toNextStep() {
    this.isBasketOpenEvent.emit(false);

    this.stepFormsService.navigateToLastEnabledPage();
  }

  private setNextButtonDisabledSignal() {
    return toSignal(
      combineLatest({
        stepsValidity: this.store.select(getStepsValidity),
        selectedStep: this.store.select(getSelectedStep)
      }).pipe(
        filter(({ stepsValidity, selectedStep }) => !isEmpty(stepsValidity) && !isEmpty(selectedStep)),
        debounceTime(100),
        map(({ stepsValidity, selectedStep }) => !stepsValidity[selectedStep]?.valid)
      ),
      { initialValue: true }
    );
  }

  private setCurrencySettingsSignal() {
    return toSignal(
      this.store.pipe(
        select(selectCurrencySettings),
        filter((currencySettings) => !isEmpty(currencySettings))
      )
    );
  }

  private setBookedProductsTotalPriceStateSignal() {
    return toSignal(
      combineLatest({
        allBookedProductsTotalPrice: this.store.select(getAllBookedProductsTotalPrice),
        isPromoCodeDiscountApplied: this.store.select(selectIsPromoCodeDiscountApplied)
      }).pipe(
        map(({ allBookedProductsTotalPrice: { totalPrice, totalDiscountedPrice }, isPromoCodeDiscountApplied }) => ({
          totalPrice,
          totalDiscountedPrice,
          isPromoCodeDiscountApplied
        }))
      )
    );
  }
}
